import React, { useState } from 'react';
import {
    Pane,
    Card,
    Text,
    Select,
    Spinner,
    Table,
    Button,
    Link,
    Alert,
    CaretUpIcon,
    CaretDownIcon
} from 'evergreen-ui';
import { formatCurrency, formatNumber } from '../../../util';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSelector } from 'react-redux';

// Add the query
const TOP_PRODUCTS_QUERY = gql`
  query TopProductsReport($publisherId: Int!, $timePeriod: String!) {
    topProductsReport(publisherId: $publisherId, timePeriod: $timePeriod)
  }
`;

const TopProductsReport = ({
    selectedTimePeriod,
    setSelectedTimePeriod,
    currentpublisher,
    loading
}) => {
    // Move all hooks to the top
    const [sortConfig, setSortConfig] = useState({
        key: 'total_amount',
        direction: 'descending'
    });

    // Get the publisher's currency from Redux store
    const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
    const currencySymbol = publisherGlobalData?.currency?.symbol || '$';

    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    const { loading: queryLoading, error, data } = useQuery(TOP_PRODUCTS_QUERY, {
        variables: {
            publisherId: parseInt(currentpublisher?.id),
            timePeriod: selectedTimePeriod
        },
        skip: !currentpublisher?.id
    });

    // Add debug logging
    console.log('TopProducts Debug:', {
        currentpublisher,
        selectedTimePeriod,
        loading: queryLoading,
        error,
        rawData: data,
        formattedData: data?.topProductsReport ? JSON.parse(data.topProductsReport) : null,
    });

    const formattedData = data?.topProductsReport ? JSON.parse(data.topProductsReport) : null;

    // Add sorting function
    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Add function to get sorted data
    const getSortedData = (data) => {
        if (!data) return [];

        return [...data].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle special cases for different fields
            if (sortConfig.key === 'total_amount' || sortConfig.key === 'units_sold') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }

            if (sortConfig.direction === 'ascending') {
                return aVal > bVal ? 1 : -1;
            }
            return aVal < bVal ? 1 : -1;
        });
    };

    // If there's an error, show it
    if (error) {
        console.error('TopProducts Error:', error);
        return <Alert intent="danger" title="Error loading top products data">{error.message}</Alert>;
    }

    return (
        <Card backgroundColor="white" elevation={1} padding={24} marginBottom={32}>
            {/* Remove the time period selector from here since it's now in the parent */}

            {loading ? (
                <Table.Row>
                    <Table.TextCell colSpan={6}>
                        <Spinner />
                    </Table.TextCell>
                </Table.Row>
            ) : (
                <Table>
                    <Table.Head>
                        <Table.TextHeaderCell
                            onClick={() => handleSort('product__title')}
                            cursor="pointer"
                        >
                            Product
                            {sortConfig.key === 'product__title' && (
                                sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                            )}
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell
                            onClick={() => handleSort('format')}
                            cursor="pointer"
                        >
                            Format
                            {sortConfig.key === 'format' && (
                                sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                            )}
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell
                            onClick={() => handleSort('creator')}
                            cursor="pointer"
                        >
                            Creator
                            {sortConfig.key === 'creator' && (
                                sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                            )}
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell
                            onClick={() => handleSort('total_amount')}
                            cursor="pointer"
                            textAlign="right"
                        >
                            Revenue
                            {sortConfig.key === 'total_amount' && (
                                sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                            )}
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell
                            onClick={() => handleSort('units_sold')}
                            cursor="pointer"
                            textAlign="right"
                        >
                            Units Sold
                            {sortConfig.key === 'units_sold' && (
                                sortConfig.direction === 'ascending' ? <CaretUpIcon /> : <CaretDownIcon />
                            )}
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell>Actions</Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body>
                        {getSortedData(formattedData?.top_products)?.map((product, index) => (
                            <Table.Row key={index}>
                                <Table.TextCell>
                                    <RouterLink
                                        to={`/products/${product.product__id}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        <Text color="blue500" cursor="pointer">
                                            {product.product__title}
                                        </Text>
                                    </RouterLink>
                                </Table.TextCell>
                                <Table.TextCell>{product.format}</Table.TextCell>
                                <Table.TextCell>{product.creator}</Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    {formatCurrencyWithSymbol(product.total_amount)}
                                </Table.TextCell>
                                <Table.TextCell textAlign="right">
                                    {formatNumber(product.units_sold)}
                                </Table.TextCell>
                                <Table.TextCell>
                                    <RouterLink
                                        to={`/reporting/profit-loss?period=qtd&products=${product.product__id}`}
                                    >
                                        <Button appearance="minimal">
                                            View P&L
                                        </Button>
                                    </RouterLink>
                                </Table.TextCell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </Card>
    );
};

export default TopProductsReport;
